import React, { useState } from 'react';
import { Layers, GitBranch, Info, Clock, Activity } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отображения зон пульса
 */
const HeartRateZones = ({ results, sportSpecific }) => {
  const [chartType, setChartType] = useState('visual'); // 'visual' или 'table'
  
  if (!results || !results.zones) {
    return null;
  }

  // Цвета для разных зон пульса
  const zoneColors = {
    0: { bg: 'bg-blue-500', text: 'text-blue-700', dark: 'dark:text-blue-300', border: 'border-blue-200', darkBg: 'dark:bg-blue-900/40' },
    1: { bg: 'bg-green-500', text: 'text-green-700', dark: 'dark:text-green-300', border: 'border-green-200', darkBg: 'dark:bg-green-900/40' },
    2: { bg: 'bg-yellow-500', text: 'text-yellow-700', dark: 'dark:text-yellow-300', border: 'border-yellow-200', darkBg: 'dark:bg-yellow-900/40' },
    3: { bg: 'bg-orange-500', text: 'text-orange-700', dark: 'dark:text-orange-300', border: 'border-orange-200', darkBg: 'dark:bg-orange-900/40' },
    4: { bg: 'bg-red-500', text: 'text-red-700', dark: 'dark:text-red-300', border: 'border-red-200', darkBg: 'dark:bg-red-900/40' }
  };

  // Получение ширины зоны в % для визуализации
  const getZoneWidth = (min, max) => {
    return ((max - min) / results.maxHeartRate) * 100;
  };

  // Получение отступа слева для позиционирования зоны
  const getZoneOffset = (min) => {
    return (min / results.maxHeartRate) * 100;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <GitBranch className="w-5 h-5 mr-2 text-red-500" />
          Зоны пульса для тренировок
        </h3>

        {/* Переключатель типа отображения */}
        <div className="flex space-x-1 border border-gray-200 dark:border-gray-700 rounded-lg">
          <button
            onClick={() => setChartType('visual')}
            className={`py-1.5 px-3 text-sm font-medium rounded-l-lg ${
              chartType === 'visual'
                ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Визуально
          </button>
          <button
            onClick={() => setChartType('table')}
            className={`py-1.5 px-3 text-sm font-medium rounded-r-lg ${
              chartType === 'table'
                ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            Таблица
          </button>
        </div>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Тренировки в разных зонах пульса помогают достичь различных целей: сжигание жира, развитие выносливости или повышение аэробной мощности. Используйте эти данные для планирования тренировок разной интенсивности.</p>
        </div>
      </div>

      {/* Визуализация зон пульса */}
      {chartType === 'visual' && (
        <div className="mb-6">
          <div className="relative h-16 mb-6">
            {results.zones.map((zone, index) => (
              <div 
                key={index}
                className={`absolute rounded-lg ${zoneColors[index].bg} opacity-80 h-full flex items-center justify-center shadow-sm overflow-hidden ${index === 0 ? 'rounded-l-xl' : ''} ${index === results.zones.length - 1 ? 'rounded-r-xl' : ''}`}
                style={{ 
                  left: `${getZoneOffset(zone.min)}%`, 
                  width: `${getZoneWidth(zone.min, zone.max)}%`,
                  minWidth: '20px'
                }}
              >
                <span className="text-white font-bold text-sm px-1 text-center">
                  {index + 1}
                </span>
              </div>
            ))}
            
            {/* Шкала пульса */}
            <div className="absolute -bottom-6 left-0 w-full flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>0</span>
              <span>{Math.round(results.maxHeartRate * 0.25)}</span>
              <span>{Math.round(results.maxHeartRate * 0.5)}</span>
              <span>{Math.round(results.maxHeartRate * 0.75)}</span>
              <span>{results.maxHeartRate}</span>
            </div>
          </div>
          
          {/* Легенда зон */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-8">
            {results.zones.map((zone, index) => (
              <div 
                key={index} 
                className={`p-3 rounded-lg ${zoneColors[index].border} ${zoneColors[index].darkBg} border`}
              >
                <div className="flex items-center mb-1">
                  <div className={`w-4 h-4 rounded-full ${zoneColors[index].bg} mr-2`}></div>
                  <h4 className={`font-medium ${zoneColors[index].text} ${zoneColors[index].dark}`}>
                    Зона {index + 1}: {zone.name}
                  </h4>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                  {zone.min} - {zone.max} уд/мин
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">
                  {zone.minPercent}% - {zone.maxPercent}% от макс.
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                  {zone.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Табличное отображение зон пульса */}
      {chartType === 'table' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-50 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-3 py-2 font-semibold">Зона</th>
                <th scope="col" className="px-3 py-2 font-semibold">Название</th>
                <th scope="col" className="px-3 py-2 font-semibold">Диапазон (уд/мин)</th>
                <th scope="col" className="px-3 py-2 font-semibold">% от макс.</th>
                <th scope="col" className="px-3 py-2 font-semibold">Описание</th>
                <th scope="col" className="px-3 py-2 font-semibold">Применение</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {results.zones.map((zone, index) => (
                <tr 
                  key={index} 
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100`}
                >
                  <td className="px-3 py-2 font-medium">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full ${zoneColors[index].bg} mr-2`}></div>
                      Зона {index + 1}
                    </div>
                  </td>
                  <td className="px-3 py-2">{zone.name}</td>
                  <td className="px-3 py-2 font-medium">{zone.min} - {zone.max}</td>
                  <td className="px-3 py-2">{zone.minPercent}% - {zone.maxPercent}%</td>
                  <td className="px-3 py-2">{zone.description}</td>
                  <td className="px-3 py-2 text-sm">
                    {index === 0 && 'Разминка, заминка, восстановление между тренировками'}
                    {index === 1 && 'Длительные тренировки на выносливость, базовое сжигание жира'}
                    {index === 2 && 'Улучшение выносливости, основная кардиотренировка'}
                    {index === 3 && 'Повышение анаэробного порога, интервальные тренировки'}
                    {index === 4 && 'Максимальные усилия, короткие спринты, не более 5 минут'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Рекомендации по активностям в разных зонах */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <Activity className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                Сжигание жира (Зоны 1-2)
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Оптимальный пульс:</strong> {results.fatBurningZone.min}-{results.fatBurningZone.max} уд/мин
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Тренировки в этих зонах эффективны для сжигания жира, развития базовой выносливости и укрепления сердечно-сосудистой системы. Рекомендуемая длительность: 30-60 минут.
              </p>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-2">
                Примеры: ходьба, легкий бег, плавание, велосипед, гребля, эллиптический тренажер
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                Повышение выносливости (Зоны 2-3)
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Оптимальный пульс:</strong> {results.cardiovascularZone.min}-{results.cardiovascularZone.max} уд/мин
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Тренировки в этих зонах помогают увеличить аэробную выносливость, повысить способность переносить длительные нагрузки и улучшить кровообращение. Рекомендуемая длительность: 20-40 минут.
              </p>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-2">
                Примеры: бег в среднем темпе, интенсивная езда на велосипеде, кардио на тренажерах, круговые тренировки
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <Activity className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                Развитие мощности (Зоны 3-4)
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Оптимальный пульс:</strong> {results.anaerobicZone.min}-{results.anaerobicZone.max} уд/мин
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Высокоинтенсивные анаэробные тренировки для повышения скоростных качеств и силовой выносливости. Тренируйтесь в этих зонах короткими интервалами. Рекомендуемая длительность: 5-15 минут с перерывами.
              </p>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-2">
                Примеры: интервальный бег, спринты, HIIT-тренировки, соревновательный темп
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                Восстановление (Зона 1)
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <strong>Оптимальный пульс:</strong> {results.zones[0].min}-{results.zones[0].max} уд/мин
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Легкие восстановительные тренировки между интенсивными нагрузками. Подходит для разминки, заминки и активного восстановления. Рекомендуемая длительность: 15-40 минут.
              </p>
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-2">
                Примеры: медленная ходьба, легкое плавание, стретчинг, йога
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Специфические рекомендации по выбранному виду спорта */}
      {sportSpecific !== 'general' && (
        <div className="mt-5 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
          <h4 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-2 flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            Рекомендации для {results.sportSpecificName}
          </h4>
          
          {sportSpecific === 'running' && (
            <div className="text-sm text-purple-600 dark:text-purple-400">
              <p>При беговых тренировках пульс обычно выше из-за вертикальной позиции тела и ударной нагрузки. Для начинающих бегунов рекомендуется большая часть тренировок в зонах 2-3 (до 75% от времени тренировок).</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                <li>Зона 1 (легкий бег): используйте для восстановительных тренировок и длительных забегов</li>
                <li>Зона 2 (аэробный бег): основная тренировочная зона для улучшения выносливости</li>
                <li>Зона 3 (темповый бег): для улучшения аэробной мощности и бегового темпа</li>
                <li>Зона 4 (интервалы): для улучшения лактатного порога и соревновательного темпа</li>
                <li>Зона 5 (спринты): только для коротких ускорений, 2-3 минуты за тренировку</li>
              </ul>
            </div>
          )}
          
          {sportSpecific === 'cycling' && (
            <div className="text-sm text-purple-600 dark:text-purple-400">
              <p>В велоспорте можно дольше поддерживать высокий пульс из-за меньшей ударной нагрузки. Используйте более высокие зоны пульса при подъемах в гору и интервальных тренировках.</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                <li>Зона 1 (легкое кручение): восстановление, разминка и длительные выезды</li>
                <li>Зона 2 (ровный темп): фундаментальная зона для развития выносливости</li>
                <li>Зона 3 (темповая езда): увеличение аэробной мощности, тренировка эффективности педалирования</li>
                <li>Зона 4 (пороговые интервалы): улучшение мощности и анаэробного порога</li>
                <li>Зона 5 (спринты): развитие максимальной мощности на короткие дистанции</li>
              </ul>
            </div>
          )}
          
          {sportSpecific === 'swimming' && (
            <div className="text-sm text-purple-600 dark:text-purple-400">
              <p>В плавании пульс обычно на 5-10 ударов ниже, чем в других видах спорта, из-за горизонтального положения тела и эффекта погружения в воду. Учитывайте это при контроле интенсивности.</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                <li>Зона 1 (легкое плавание): техническая работа, восстановление</li>
                <li>Зона 2 (аэробное плавание): основной объем тренировок, развитие выносливости</li>
                <li>Зона 3 (темповое плавание): улучшение техники на повышенных скоростях</li>
                <li>Зона 4 (интервальная работа): улучшение лактатного порога и скорости</li>
                <li>Зона 5 (спринт): максимальные усилия на короткие отрезки</li>
              </ul>
            </div>
          )}
          
          {sportSpecific === 'strength' && (
            <div className="text-sm text-purple-600 dark:text-purple-400">
              <p>При силовых тренировках пульс меняется скачкообразно и может быстро достигать высоких значений. Важно контролировать периоды отдыха между подходами (пульс должен снижаться до зоны 2).</p>
              <ul className="list-disc list-inside mt-2 space-y-1 text-xs">
                <li>Зона 1: разминка и заминка, восстановление между тяжелыми тренировками</li>
                <li>Зона 2: круговые тренировки на выносливость, большое количество повторений</li>
                <li>Зона 3: гипертрофийные тренировки, оптимальная зона для роста мышц</li>
                <li>Зона 4: силовые комплексы высокой интенсивности, HIIT с отягощениями</li>
                <li>Зона 5: максимальные усилия, не более 1-2 минут на пиковых нагрузках</li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeartRateZones;